import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { PATHS } from '../paths';
import { AccidentCreatingStepDecision } from './accidents/AccidentCreatingStepDecision';
import { ACCIDENT_SOURCE_TYPE } from './accidents/constants';
import { PageLayout } from './common';
import { useAuth } from './hooks';
import {
  ACCIDENT_SOURCE,
  AccidentGeneral,
  AccidentSourcesList,
  RegistryType,
  RegistryTypeLabel
} from './types/accident';

interface AccidentDecisionPage {
  handleUserChoice: (choice: AccidentGeneral) => void;
  isLoading?: boolean;
  acceptanceReasons?: AccidentSourcesList[];
  rejectionReasons?: RegistryTypeLabel[];
}

const AccidentDecisionPageContent = ({ handleUserChoice, isLoading, ...props }: AccidentDecisionPage) => (
  <PageLayout title="Založení nehody">
    <AccidentCreatingStepDecision onVote={handleUserChoice} isLoading={isLoading} {...props} />
  </PageLayout>
);

export const AccidentDecisionPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const commonApi = useCommonApi();

  const { mutate: createNewAccident, isPending: isCreatingAccident } = useMutation(commonApi.createAccident());

  const { data: acceptanceSources } = useQuery<AccidentSourcesList[]>(
    useCommonApi().getRegistryList(RegistryType.ACCIDENTS_SOURCES)
  );
  const { data: rejectionSources } = useQuery<RegistryTypeLabel[]>(
    useCommonApi().getRegistryList(RegistryType.ACCIDENT_REJECTIONS)
  );

  const handleCreateNewAccident = (data: AccidentGeneral) => {
    createNewAccident(
      {
        dealerId: Number.parseInt(user?.id ?? ''),
        ...data
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: (_, { id }) => {
            !data.isRejected
              ? navigate(PATHS.NEW_ACCIDENT(id))
              : navigate(PATHS.ACCIDENT_CONFIRM(id, !data.isRejected));

            // Invalidate accident sources query to reflect any changes
            queryClient.resetQueries({
              queryKey: commonApi.getAccidentSourcesByType(
                data.source?.type === ACCIDENT_SOURCE_TYPE.SERVICE ? ACCIDENT_SOURCE.SERVICE : ACCIDENT_SOURCE.TIPPER
              ).queryKey
            });
          }
        })
      }
    );
  };

  return (
    <AccidentDecisionPageContent
      handleUserChoice={handleCreateNewAccident}
      isLoading={isCreatingAccident}
      acceptanceReasons={acceptanceSources}
      rejectionReasons={rejectionSources}
    />
  );
};
